import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Edit,
  Download,
  Search,
  Eye,
  Plus,
  Building,
  User,
  CheckCircle,
  AlertCircle,
  X,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileText
} from 'lucide-react';
import doiService from '../../../api/crm/doi/doiService';
import PermissionGate from '../../../components/common/PermissionGate';
import opportunityService from '../../../api/crm/opportunities/opportunityService';
import customerService from '../../../api/crm/customers/customerService';
import { formatDate } from "../../../utils/stringUtils";
import { formatIndianAmount, formatIndianNumber } from "../../../utils/amountFormatter";
import ExportModal from '../../../components/common/ExportModal';
import { exportToCSV } from '../../../utils/exportUtils';
import { toast } from 'react-hot-toast';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import apiClient from '../../../api/apiClient';
import { USER_ROLES } from '../../../utils/constants';
import { usePermissions } from '../../../hooks/usePermissions';

const CreateQuotesList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { userRole } = usePermissions();
  const isAdminRole = [USER_ROLES.SUPER_ADMIN, 'admin'].includes(userRole);

  // Quote list pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Sorting state
  const [sortColumn, setSortColumn] = useState('created');
  const [sortDirection, setSortDirection] = useState('desc');

  // Bulk delete state
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [deletingDoiId, setDeletingDoiId] = useState(null);
  const [doiToDelete, setDoiToDelete] = useState(null);


  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Selection & Export State
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [createdByFilter, setCreatedByFilter] = useState(null);
  const [createdBySearchTerm, setCreatedBySearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });

  // Opportunity Selection Modal states
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const searchInputRef = useRef(null); // Ref for search input focus
  const [modalActiveTab, setModalActiveTab] = useState('lead'); // 'lead' or 'customers'
  const [opportunitySearchTerm, setOpportunitySearchTerm] = useState('');
  const [debouncedOpportunitySearchTerm, setDebouncedOpportunitySearchTerm] = useState('');
  const [opportunityPagination, setOpportunityPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalOpportunities: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [opportunityItemsPerPage, setOpportunityItemsPerPage] = useState(10);

  // Customer Selection Modal states
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [debouncedCustomerSearchTerm, setDebouncedCustomerSearchTerm] = useState('');
  const [customerPagination, setCustomerPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalCustomers: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [customerItemsPerPage, setCustomerItemsPerPage] = useState(10);

  // Fetch KPIs - Removed for DOI (not applicable)

  // Debounce search term for DOIs
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchQuery);
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Debounce search term for opportunities
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedOpportunitySearchTerm(opportunitySearchTerm);
      setOpportunityPagination(prev => ({ ...prev, page: 1 }));
    }, 300);

    return () => clearTimeout(timer);
  }, [opportunitySearchTerm]);

  // Debounce search term for customers
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomerSearchTerm(customerSearchTerm);
      setCustomerPagination(prev => ({ ...prev, page: 1 }));
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearchTerm]);

  // React Query: Fetch all created quotes
  const {
    data: quotesData,
    isLoading: loadingQuotes,
    isError: quotesError,
    error: quotesErrorData
  } = useQuery({
    queryKey: ['doiList', debouncedSearchTerm, createdByFilter, currentPage, itemsPerPage, dateRange, refreshKey],
    queryFn: async () => {
      const params = {
        search: debouncedSearchTerm.trim() || undefined,
        createdBy: createdByFilter || undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        page: currentPage,
        limit: itemsPerPage,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };

      const result = await doiService.getAllDois(params);

      if (result.success) {
        return {
          quotes: result.dois || [],
          pagination: result.pagination || null
        };
      } else {
        throw new Error(result.error || 'Failed to fetch DOIs');
      }
    },
    staleTime: 0,
    retry: 2
  });

  const {
    data: createdByUsersData,
    isLoading: loadingCreatedByUsers
  } = useQuery({
    queryKey: ['doiCreatedByUsers', createdBySearchTerm],
    queryFn: async () => {
      const response = await apiClient.get('/dropdown-data/employees', {
        params: {
          search: createdBySearchTerm || undefined,
          limit: 50,
          includeSuperAdmin: true,
          activeOnly: true
        }
      });

      return response.data?.employees || [];
    },
    staleTime: 60000,
    retry: 1
  });

  const createdByOptions = useMemo(() => (
    (createdByUsersData || []).map((employee) => {
      const firstName = employee.personalInfo?.firstName || employee.firstName || '';
      const lastName = employee.personalInfo?.lastName || employee.lastName || '';
      const name = `${firstName} ${lastName}`.trim() || employee.fullName || employee.name || 'Unnamed User';
      const email = employee.officialEmail || employee.email || employee.contactInfo?.personalEmail || '';
      const position = employee.employmentInfo?.position || employee.position || employee.role || '';

      return {
        id: employee.id || employee._id,
        displayText: email ? `${name} - ${email}` : name,
        searchText: `${name} ${email} ${employee.employeeId || ''}`.toLowerCase(),
        title: name,
        raw: { name, email, position, employeeId: employee.employeeId },
        renderOption: (option) => (
          <div className="flex flex-col py-1">
            <span className="font-semibold text-gray-800">{option.raw.name}</span>
            {option.raw.email && <span className="text-sm text-gray-500">{option.raw.email}</span>}
            <span className="text-sm text-gray-500">{option.raw.employeeId || option.raw.position || 'User'}</span>
          </div>
        )
      };
    })
  ), [createdByUsersData]);

  const quotes = quotesData?.quotes || [];
  const pagination = quotesData?.pagination || null;

  // React Query: Fetch opportunities without quotes for modal
  const {
    data: opportunitiesData,
    isLoading: loadingOpportunities,
    isError: opportunitiesError,
    error: opportunitiesErrorData,
    refetch: refetchOpportunities
  } = useQuery({
    queryKey: ['opportunities', 'list', 'withoutQuotations', debouncedOpportunitySearchTerm, opportunityPagination.page, opportunityItemsPerPage],
    queryFn: async () => {
      const result = await opportunityService.getConvertedOpportunitiesWithoutQuotations({
        search: debouncedOpportunitySearchTerm.trim() || undefined,
        page: opportunityPagination.page,
        limit: opportunityItemsPerPage
      });

      if (result.success) {
        return {
          opportunities: result.opportunities || [],
          pagination: result.pagination || {
            page: opportunityPagination.page,
            limit: opportunityItemsPerPage,
            totalPages: 1,
            totalOpportunities: 0,
            hasNextPage: false,
            hasPrevPage: false
          }
        };
      } else {
        throw new Error(result.error || 'Failed to load opportunities');
      }
    },
    enabled: showOpportunityModal && modalActiveTab === 'lead', // Only fetch when modal is open and Lead tab is active
    staleTime: 0,
    retry: 2
  });

  // React Query: Fetch customers for modal
  const {
    data: customersData,
    isLoading: loadingCustomers,
    isError: customersError,
    error: customersErrorData,
    refetch: refetchCustomers
  } = useQuery({
    queryKey: ['customers', 'list', debouncedCustomerSearchTerm, customerPagination.page, customerItemsPerPage],
    queryFn: async () => {
      const result = await customerService.getCustomers({
        search: debouncedCustomerSearchTerm.trim() || undefined,
        status: 'customer', // Only fetch active customers
        page: customerPagination.page,
        limit: customerItemsPerPage
      });

      if (result.success) {
        return {
          customers: result.customers || [],
          pagination: result.pagination || {
            page: customerPagination.page,
            limit: customerItemsPerPage,
            totalPages: 1,
            total: 0
          }
        };
      } else {
        throw new Error(result.error || 'Failed to load customers');
      }
    },
    enabled: showOpportunityModal && modalActiveTab === 'customers', // Only fetch when modal is open and Customers tab is active
    staleTime: 0,
    retry: 2
  });

  const opportunities = opportunitiesData?.opportunities || [];
  const opportunityPaginationData = opportunitiesData?.pagination || opportunityPagination;
  const opportunityError = opportunitiesError ? (opportunitiesErrorData?.message || 'Failed to load opportunities. Please try again.') : null;

  const customers = customersData?.customers || [];
  const customerPaginationData = customersData?.pagination || customerPagination;
  const customerError = customersError ? (customersErrorData?.message || 'Failed to load customers. Please try again.') : null;

  // Update opportunity pagination state when data changes
  useEffect(() => {
    if (opportunitiesData?.pagination) {
      setOpportunityPagination(prev => ({
        page: opportunitiesData.pagination.currentPage || prev.page,
        limit: opportunitiesData.pagination.limit || prev.limit,
        totalPages: opportunitiesData.pagination.totalPages || 1,
        totalOpportunities: opportunitiesData.pagination.totalOpportunities || 0,
        hasNextPage: opportunitiesData.pagination.hasNextPage || false,
        hasPrevPage: opportunitiesData.pagination.hasPrevPage || false
      }));
    }
  }, [opportunitiesData]);

  // Update customer pagination state when data changes
  useEffect(() => {
    if (customersData?.pagination) {
      const total = customersData.pagination.total || 0;
      const totalPages = customersData.pagination.totalPages || 1;
      const currentPage = customersData.pagination.page || customerPagination.page;

      setCustomerPagination(prev => ({
        page: currentPage,
        limit: customersData.pagination.limit || prev.limit,
        totalPages: totalPages,
        totalCustomers: total,
        hasNextPage: currentPage < totalPages,
        hasPrevPage: currentPage > 1
      }));
    }
  }, [customersData]);

  // Skeleton loading components
  const TableRowSkeleton = () => (
    <tr className="animate-pulse">
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-4"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
      <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          <div className="h-8 bg-gray-200 rounded w-20"></div>
          <div className="h-8 bg-gray-200 rounded w-24"></div>
        </div>
      </td>
    </tr>
  );

  const OpportunityRowSkeleton = () => (
    <div className="p-4 border-b border-gray-200 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-32"></div>
        </div>
        <div className="h-8 bg-gray-200 rounded w-24"></div>
      </div>
    </div>
  );

  const handleUpdateQuote = (quote) => {
    const idToUse = quote._id || quote.quoteId || quote.id;
    // Navigate to quote Builder with doiId for editing
    navigate(`/crm/quotation-proposal/quote-builder?doiId=${idToUse}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1); // Reset to page 1 when changing items per page
  };

  // Selection handlers
  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (items) => {
    if (items.length === 0) {
      setSelectedRows(new Set());
      return;
    }

    // items is the current page data
    const allSelected = items.every(item => selectedRows.has(item._id || item.quoteId));

    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (allSelected) {
        items.forEach(item => newSelection.delete(item._id || item.quoteId));
      } else {
        items.forEach(item => newSelection.add(item._id || item.quoteId));
      }
      return newSelection;
    });
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      handleExportCurrentPage();
    } else {
      setShowExportModal(true);
    }
  };

  const getCreatorInfo = (doi) => {
    const creator = doi.createdBy;
    if (!creator) return { name: 'N/A', email: '' };
    if (typeof creator === 'string') return { name: creator, email: '' };

    const name = [creator.firstName, creator.lastName].filter(Boolean).join(' ') || creator.name || creator.email || 'N/A';
    return { name, email: creator.email || '' };
  };

  const mapQuoteToExport = (doi) => {
    const createdDate = doi.createdAt || doi.created_at;
    const formattedDate = createdDate ? formatDate(createdDate, { includeTime: false }) : 'N/A';
    const creator = getCreatorInfo(doi);

    return {
      'DOI Number': doi.doiNumber || 'N/A',
      'Project Name': doi.projectName || 'N/A',
      'Customer': doi.mainCustomerNames || doi.customerId?.companyName || 'N/A',
      'Value': `₹${doi.projectOrderValue?.amount ? doi.projectOrderValue.amount.toLocaleString('en-IN') : '0'}`,
      'Created By': creator.email ? `${creator.name} (${creator.email})` : creator.name,
      'Created Date': formattedDate
    };
  };

  const handleExportCurrentPage = () => {
    try {
      // Filter quotes if selection exists
      const dataToExport = selectedRows.size > 0
        ? quotes.filter(quote => selectedRows.has(quote._id || quote.quoteId))
        : quotes;

      if (dataToExport.length === 0) {
        toast.error('No data to export');
        return;
      }

      const type = selectedRows.size > 0 ? 'Selected Items' : 'Current Page';
      const formattedData = dataToExport.map(mapQuoteToExport);
      const headers = ['DOI Number', 'Project Name', 'Customer', 'Value', 'Created By', 'Created Date'];

      exportToCSV(formattedData, 'create_dois_list', { title: 'DOIs', type }, headers);
      toast.success('DOIs exported successfully');
      setSelectedRows(new Set());
    } catch (error) {

      toast.error('Failed to export DOIs');
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      // Fetch larger set for "All" export with current filters
      const params = {
        search: debouncedSearchTerm.trim() || undefined,
        createdBy: createdByFilter || undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        page: 1,
        limit: 1000 // Reasonable limit
      };

      const result = await doiService.getAllDois(params);

      if (result.success && result.dois.length > 0) {
        const mappedData = result.dois.map(mapQuoteToExport);
        const headers = ['DOI Number', 'Project Name', 'Customer', 'Value', 'Created By', 'Created Date'];

        exportToCSV(mappedData, 'dois_all', { title: 'All DOIs', type: 'All Data' }, headers);
        toast.success(`Exported ${result.dois.length} DOIs successfully`);
        setShowExportModal(false);
      } else {
        toast.error('No data to export');
      }
    } catch (error) {
      toast.error('Failed to export DOIs');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const params = {
        startDate,
        endDate,
        search: debouncedSearchTerm.trim() || undefined,
        createdBy: createdByFilter || undefined,
        page: 1,
        limit: 10000
      };

      const result = await doiService.getAllDois(params);

      if (result.success && result.dois.length > 0) {
        const mappedData = result.dois.map(mapQuoteToExport);
        const headers = ['DOI Number', 'Project Name', 'Customer', 'Value', 'Created By', 'Created Date'];
        const type = `Date Range (${startDate} to ${endDate})`;

        exportToCSV(mappedData, 'dois_range', { title: 'DOIs', type }, headers);
        toast.success(`Exported ${result.dois.length} DOIs successfully`);
        setShowExportModal(false);
      } else {
        toast.error('No DOIs found in the selected range');
      }
    } catch (error) {
      toast.error('Failed to export DOIs');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle items per page change for opportunities
  const handleOpportunityItemsPerPageChange = (limit) => {
    setOpportunityItemsPerPage(limit);
    setOpportunityPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleOpenOpportunityModal = () => {
    // Reset all states first
    setModalActiveTab('lead');
    setOpportunitySearchTerm('');
    setDebouncedOpportunitySearchTerm('');
    setCustomerSearchTerm('');
    setDebouncedCustomerSearchTerm('');
    setOpportunityPagination({
      page: 1,
      limit: 10,
      totalPages: 1,
      totalOpportunities: 0,
      hasNextPage: false,
      hasPrevPage: false
    });
    setCustomerPagination({
      page: 1,
      limit: 10,
      totalPages: 1,
      totalCustomers: 0,
      hasNextPage: false,
      hasPrevPage: false
    });
    setOpportunityItemsPerPage(10);
    setCustomerItemsPerPage(10);
    // Open modal - React Query will handle the API call
    setShowOpportunityModal(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('createDoi') === '1') {
      handleOpenOpportunityModal();
      params.delete('createDoi');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const handleCloseOpportunityModal = () => {
    setShowOpportunityModal(false);
    setModalActiveTab('lead');
    setOpportunitySearchTerm('');
    setDebouncedOpportunitySearchTerm('');
    setCustomerSearchTerm('');
    setDebouncedCustomerSearchTerm('');
    setOpportunityPagination({
      page: 1,
      limit: 10,
      totalPages: 1,
      totalOpportunities: 0,
      hasNextPage: false,
      hasPrevPage: false
    });
    setCustomerPagination({
      page: 1,
      limit: 10,
      totalPages: 1,
      totalCustomers: 0,
      hasNextPage: false,
      hasPrevPage: false
    });
    setOpportunityItemsPerPage(10);
    setCustomerItemsPerPage(10);
  };

  // Handle items per page change for customers
  const handleCustomerItemsPerPageChange = (limit) => {
    setCustomerItemsPerPage(limit);
    setCustomerPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSelectOpportunity = (opportunityId) => {
    handleCloseOpportunityModal();
    navigate(`/crm/quotation-proposal/quote-builder?opportunityId=${opportunityId}`);
  };

  const handleSelectCustomer = (customerId) => {
    handleCloseOpportunityModal();
    navigate(`/crm/quotation-proposal/quote-builder?customerId=${customerId}`);
  };

  // Bulk delete handlers
  const handleBulkDeleteClick = () => {
    if (!isAdminRole) return;

    if (selectedRows.size > 0) {
      setShowBulkDeleteModal(true);
    }
  };

  const confirmBulkDelete = async () => {
    if (!isAdminRole || selectedRows.size === 0) return;
    setIsBulkDeleting(true);
    const ids = Array.from(selectedRows);
    try {
      const results = await Promise.allSettled(
        ids.map(id => doiService.deleteDoi(id))
      );
      const succeeded = results.filter(r => r.status === 'fulfilled' && r.value?.success).length;
      const failed = ids.length - succeeded;

      queryClient.invalidateQueries(['doiList']);
      setSelectedRows(new Set());
      setShowBulkDeleteModal(false);

      if (failed === 0) {
        toast.success(`${succeeded} DOI${succeeded > 1 ? 's' : ''} deleted successfully!`);
      } else {
        toast.error(`${succeeded} deleted, ${failed} failed. Please try again.`);
      }
    } catch (error) {
      toast.error('Failed to delete DOIs');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleDeleteQuote = (quote) => {
    if (!isAdminRole) return;

    const doiId = quote._id || quote.id || quote.doiId || quote.doiNumber;
    if (!doiId) {
      toast.error('Unable to delete DOI. Missing DOI identifier.');
      return;
    }

    setDoiToDelete({
      ...quote,
      deleteId: doiId
    });
  };

  const handleCancelDeleteQuote = () => {
    if (deletingDoiId) return;
    setDoiToDelete(null);
  };

  const confirmDeleteQuote = async () => {
    if (!isAdminRole || !doiToDelete?.deleteId) return;

    const doiId = doiToDelete.deleteId;
    setDeletingDoiId(doiId);
    try {
      const result = await doiService.deleteDoi(doiId);
      if (result.success) {
        toast.success('DOI deleted successfully');
        setSelectedRows(prev => {
          const newSelection = new Set(prev);
          newSelection.delete(doiId);
          newSelection.delete(doiToDelete.doiNumber);
          return newSelection;
        });
        setDoiToDelete(null);
        queryClient.invalidateQueries(['doiList']);
      } else {
        toast.error(result.error || 'Failed to delete DOI');
      }
    } catch (error) {
      toast.error('Failed to delete DOI');
    } finally {
      setDeletingDoiId(null);
    }
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted quotes for the table
  const getSortedQuotes = () => {
    if (!sortColumn || !quotes || quotes.length === 0) return quotes;

    return [...quotes].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'doiNumber') {
        aValue = a.doiNumber || '';
        bValue = b.doiNumber || '';
      } else if (sortColumn === 'projectName') {
        aValue = a.projectName || '';
        bValue = b.projectName || '';
      } else if (sortColumn === 'mainCustomerNames') {
        aValue = a.mainCustomerNames || a.customerId?.companyName || '';
        bValue = b.mainCustomerNames || b.customerId?.companyName || '';
      } else if (sortColumn === 'projectOrderValue') {
        aValue = Number(a.projectOrderValue?.amount) || 0;
        bValue = Number(b.projectOrderValue?.amount) || 0;
      } else if (sortColumn === 'createdBy') {
        aValue = getCreatorInfo(a).name;
        bValue = getCreatorInfo(b).name;
      } else if (sortColumn === 'created') {
        aValue = new Date(a.createdAt || a.created_at || 0).getTime();
        bValue = new Date(b.createdAt || b.created_at || 0).getTime();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">DOI Management</h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">Manage DOI records, communication, and supporting documents</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdminRole && selectedRows.size > 0 && (
            <PermissionGate permission="quotation_management" action="delete">
              <button
                onClick={handleBulkDeleteClick}
                disabled={isBulkDeleting}
                className="flex items-center justify-center space-x-1.5 !bg-red-600 hover:!bg-red-700 !text-white rounded-md px-3 py-1.5 text-[13px] font-medium h-8 shadow-sm transition-colors"
              >
                {isBulkDeleting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span>Delete ({selectedRows.size})</span>
              </button>
            </PermissionGate>
          )}
          <PermissionGate permission="quotation_management" action="read">
            <button
              onClick={handleExportClick}
              className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              <Download className="h-4 w-4" />
              <span>{selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}</span>
            </button>
          </PermissionGate>
          <PermissionGate permission="quotation_management" action="create">
            <button
              onClick={handleOpenOpportunityModal}
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
            >
              <Plus className="h-4 w-4" />
              <span>Create DOI</span>
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-row flex-wrap items-center gap-3 py-1 overflow-visible">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by DOI ID, company..."
              className="input-field pl-10"
            />
          </div>
          <div className="relative z-20 flex-shrink-0 w-full sm:w-[240px]">
            <SearchableDropdown
              options={createdByOptions}
              value={createdByFilter}
              onChange={(selectedId) => {
                setCreatedByFilter(selectedId);
                setCurrentPage(1);
              }}
              onSearch={(searchTerm) => setCreatedBySearchTerm(searchTerm)}
              placeholder={loadingCreatedByUsers ? 'Loading creators...' : 'Created By'}
              loading={loadingCreatedByUsers}
              searchPlaceholder="Search users..."
              minSearchLength={1}
              debounceMs={300}
            />
          </div>
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(newRange) => {
                setDateRange(newRange);
                setCurrentPage(1);
              }}
              allowFutureDates={true}
            />
          </div>
          {(searchQuery || createdByFilter || dateRange.startDate || dateRange.endDate) && (
            <button
              onClick={() => {
                setSearchQuery('');
                setCreatedByFilter(null);
                setCreatedBySearchTerm('');
                setDateRange({ startDate: '', endDate: '' });
                setCurrentPage(1);
              }}
              className="btn-cancel flex-shrink-0 whitespace-nowrap flex items-center justify-center space-x-2"
            >
              <X className="h-4 w-4" />
              <span>Clear</span>
            </button>
          )}
        </div>
      </div>


      {/* DOIs Table */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded cursor-pointer"
                      checked={getSortedQuotes().length > 0 && getSortedQuotes().every(quote => selectedRows.has(quote._id || quote.quoteId))}
                      onChange={() => handleSelectAll(getSortedQuotes())}
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('doiNumber')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>DOI Number</span>
                      {sortColumn === 'doiNumber' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('projectName')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Project Name</span>
                      {sortColumn === 'projectName' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('mainCustomerNames')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Customer</span>
                      {sortColumn === 'mainCustomerNames' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('projectOrderValue')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Value</span>
                      {sortColumn === 'projectOrderValue' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('createdBy')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created By</span>
                      {sortColumn === 'createdBy' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('created')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created Date</span>
                      {sortColumn === 'created' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loadingQuotes ? (
                  <>
                    {[...Array(5)].map((_, index) => (
                      <TableRowSkeleton key={index} />
                    ))}
                  </>
                ) : quotes.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-sm text-gray-500">
                      No DOIs found
                    </td>
                  </tr>
                ) : (
                  getSortedQuotes().map((quote) => {
                    const createdDate = quote.createdAt || quote.created_at;
                    const formattedDate = createdDate ? formatDate(createdDate, { includeTime: true }) : 'N/A';
                    const creator = getCreatorInfo(quote);

                    return (
                      <tr key={quote.quoteId || quote._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded cursor-pointer"
                            checked={selectedRows.has(quote._id || quote.doiNumber)}
                            onChange={() => handleSelectRow(quote._id || quote.doiNumber)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div className="flex items-center">
                            {quote.doiNumber || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap truncate max-w-40 text-sm text-gray-900" title={quote.projectName || 'N/A'}>
                          {quote.projectName || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap truncate max-w-32 text-sm text-gray-900 capitalize" title={quote.mainCustomerNames || quote.customerId?.companyName || 'N/A'}>
                          {quote.mainCustomerNames || quote.customerId?.companyName || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ₹{formatIndianAmount(quote.projectOrderValue?.amount) || '0'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="max-w-44" title={creator.email || creator.name}>
                            <div className="truncate font-medium text-gray-700">{creator.name}</div>
                            {creator.email && <div className="truncate text-xs text-gray-500">{creator.email}</div>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formattedDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            {/* View button */}
                            <PermissionGate permission="quotation_management" action="read">
                              <button
                                onClick={() => navigate(`/crm/quotation-proposal/doi-view?doiId=${quote._id}`)}
                                className="text-gray-600 hover:text-gray-900"
                                title="View DOI"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>

                            {/* Update button */}
                            <PermissionGate permission="quotation_management" action="update">
                              <button
                                onClick={() => handleUpdateQuote(quote)}
                                className="text-green-600 hover:text-green-900"
                                title="Update DOI"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </PermissionGate>

                            {isAdminRole && (
                              <PermissionGate permission="quotation_management" action="delete">
                                <button
                                  onClick={() => handleDeleteQuote(quote)}
                                  className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                  disabled={deletingDoiId === (quote._id || quote.id || quote.doiId || quote.doiNumber)}
                                  title={deletingDoiId === (quote._id || quote.id || quote.doiId || quote.doiNumber) ? 'Deleting DOI' : 'Delete DOI'}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}

                          </div>
                        </td>
                      </tr>);
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loadingQuotes && (
            <PaginationControls
              currentPage={pagination?.page || currentPage}
              totalPages={pagination?.totalPages || 1}
              totalItems={pagination?.totalRecords || quotes.length || 0}
              limit={pagination?.limit || itemsPerPage}
              hasNextPage={pagination?.hasNextPage ?? (currentPage < (pagination?.totalPages || 1))}
              hasPrevPage={pagination?.hasPrevPage ?? (currentPage > 1)}
              onPageChange={handlePageChange}
              onLimitChange={handleItemsPerPageChange}
            />
          )}
        </div>
      </div>

      {/* Opportunity Selection Modal */}
      {
        showOpportunityModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl mx-auto max-h-[90vh] overflow-hidden flex flex-col modal-body">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Select for DOI</h2>
                  <p className="text-sm text-gray-600 mt-1">Choose a lead opportunity or customer to create a DOI</p>
                </div>
                <button
                  onClick={handleCloseOpportunityModal}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Tabs */}
              <div className="px-6 pt-4 border-b border-gray-200">
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setModalActiveTab('lead')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${modalActiveTab === 'lead'
                      ? 'border-brand text-brand'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    Opportunities
                  </button>
                  <button
                    type="button"
                    onClick={() => setModalActiveTab('customers')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${modalActiveTab === 'customers'
                      ? 'border-brand text-brand'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    Customers
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-6 modal-body">
                {/* Search Bar */}
                <div className="mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      autoFocus
                      placeholder={
                        modalActiveTab === 'lead'
                          ? "Search by Company Name, Lead Name, Email, or Phone"
                          : "Search by Customer Name, Company Name, Email, Phone, or Customer ID"
                      }
                      value={modalActiveTab === 'lead' ? opportunitySearchTerm : customerSearchTerm}
                      onChange={(e) => {
                        if (modalActiveTab === 'lead') {
                          setOpportunitySearchTerm(e.target.value);
                        } else {
                          setCustomerSearchTerm(e.target.value);
                        }
                      }}
                      className="input-field pl-10"
                    />
                  </div>
                </div>

                {/* Lead Tab Content */}
                {modalActiveTab === 'lead' && (
                  <>
                    {/* Loading State */}
                    {loadingOpportunities && (
                      <div className="divide-y divide-gray-200">
                        {[...Array(5)].map((_, index) => (
                          <OpportunityRowSkeleton key={index} />
                        ))}
                      </div>
                    )}

                    {/* Error State */}
                    {opportunityError && !loadingOpportunities && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <div className="flex items-start space-x-3">
                          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                          <div>
                            <h3 className="text-sm font-medium text-red-800">Error Loading Opportunities</h3>
                            <p className="text-sm text-red-700 mt-1">{opportunityError}</p>
                            <button
                              onClick={() => refetchOpportunities()}
                              className="btn-cancel mt-4"
                            >
                              Retry
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Empty State */}
                    {!loadingOpportunities && !opportunityError && opportunities.length === 0 && (
                      <div className="text-center py-12">
                        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {opportunitySearchTerm.trim() ? 'No opportunities found' : 'No Opportunities Available'}
                        </h3>
                        <p className="text-gray-600">
                          {opportunitySearchTerm.trim()
                            ? `No opportunities match your search "${opportunitySearchTerm}". Try adjusting your search terms.`
                            : 'There are no opportunities available for creating DOIs. All opportunities may already have DOIs created for them, or there are no active opportunities at this time.'}
                        </p>
                      </div>
                    )}

                    {/* Opportunities List */}
                    {!loadingOpportunities && !opportunityError && opportunities.length > 0 && (
                      <div className="space-y-3">
                        {opportunities.map((opportunity) => {
                          const companyName = opportunity.leadId?.company ||
                            opportunity.customer?.companyName ||
                            opportunity.customer?.name ||
                            'Unknown Company';
                          const contactName = `${opportunity.leadId?.firstName || ''} ${opportunity.leadId?.lastName || ''}`.trim() || 'N/A';
                          const contactEmail = opportunity.leadId?.email || '';
                          const opportunityValue = '₹' + formatIndianAmount(opportunity.amount);

                          return (
                            <div
                              key={opportunity._id || opportunity.id}
                              onClick={() => {
                                // Since backend already filters out opportunities with existing records,
                                // all displayed opportunities are eligible for DOI creation.
                                handleSelectOpportunity(opportunity._id || opportunity.id);
                              }}
                              className={`
                          border border-gray-200 rounded-lg p-4 transition-all duration-200 opportunity-card
                          bg-white hover:bg-brand/5 hover:border-brand cursor-pointer hover:shadow-md
                        `}
                            >
                              <div className="flex items-start justify-between">
                                {/* Left Section - Company & Contact Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-1">
                                      <Building className="h-5 w-5 text-brand" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h3
                                        className="text-base font-semibold text-gray-900 truncate opportunity-title capitalize"
                                        title={companyName}
                                      >
                                        {companyName}
                                      </h3>
                                      <div className="mt-2 flex flex-wrap items-center gap-4">
                                        <div className="flex items-center space-x-2 min-w-0">
                                          <User className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                          <span
                                            className="text-sm text-gray-700 truncate opportunity-title capitalize"
                                            title={contactName}
                                          >
                                            {contactName}
                                          </span>
                                        </div>
                                        {contactEmail && (
                                          <span
                                            className="text-xs text-gray-500 truncate opportunity-title"
                                            title={contactEmail}
                                          >
                                            {contactEmail}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Right Section - Value & Status */}
                                <div className="flex items-center space-x-4 ml-4 flex-shrink-0">
                                  <div className="text-right min-w-0">
                                    <div
                                      className="text-lg font-bold text-brand truncate opportunity-title"
                                      title={opportunityValue}
                                    >
                                      {opportunityValue}
                                    </div>
                                    {/* Backend filters out records that are already created. */}
                                  </div>
                                  <div className="flex-shrink-0">
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand/10 text-brand">
                                      <CheckCircle className="h-5 w-5" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                          );
                        })}
                      </div>
                    )}

                    {/* Pagination */}
                    {!loadingOpportunities && !opportunityError && opportunities.length > 0 && (() => {
                      const totalItems = opportunityPaginationData.totalOpportunities || opportunities.length;
                      const totalPages = opportunityPaginationData.totalPages || 1;
                      const effectiveLimit = opportunityPaginationData.limit || opportunityItemsPerPage;
                      const shouldShowPagination = totalPages > 1;
                      const currentPage = opportunityPaginationData.page || opportunityPagination.page || 1;

                      const handlePageChange = (page) => {
                        setOpportunityPagination(prev => ({ ...prev, page }));
                      };

                      if (!shouldShowPagination) return null;

                      // Generate page numbers
                      const pages = [];
                      if (totalPages <= 10) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push(1);
                        if (currentPage <= 4) {
                          for (let i = 2; i <= 5; i++) {
                            pages.push(i);
                          }
                          pages.push('ellipsis');
                          pages.push(totalPages);
                        } else if (currentPage >= totalPages - 3) {
                          pages.push('ellipsis');
                          for (let i = totalPages - 4; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push('ellipsis');
                          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                            pages.push(i);
                          }
                          pages.push('ellipsis');
                          pages.push(totalPages);
                        }
                      }

                      return (
                        <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                          <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                            {/* Results Count and Limit Selector */}
                            <div className="flex items-center space-x-4">
                              <div className="text-sm text-gray-700">
                                Showing {formatIndianNumber(((currentPage - 1) * effectiveLimit) + 1)} to {formatIndianNumber(Math.min(currentPage * effectiveLimit, totalItems))} of {formatIndianNumber(totalItems)} results
                              </div>
                              <div className="flex items-center space-x-2">
                                <label className="text-sm text-gray-700">Items per page:</label>
                                <select
                                  value={opportunityItemsPerPage}
                                  onChange={(e) => handleOpportunityItemsPerPageChange(Number(e.target.value))}
                                  className="input-field px-2 py-1 text-sm w-20"
                                >
                                  <option value={3}>3</option>
                                  <option value={10}>10</option>
                                  <option value={25}>25</option>
                                  <option value={50}>50</option>
                                  <option value={100}>100</option>
                                </select>
                              </div>
                            </div>

                            {/* Pagination Controls */}
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={!opportunityPagination.hasPrevPage || currentPage <= 1}
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Previous
                              </button>

                              {/* Page Numbers */}
                              <div className="flex items-center space-x-1">
                                {pages.map((page, index) => {
                                  if (page === 'ellipsis') {
                                    return (
                                      <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                        ...
                                      </span>
                                    );
                                  }

                                  return (
                                    <button
                                      key={page}
                                      onClick={() => setOpportunityPagination(prev => ({ ...prev, page }))}
                                      className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPage
                                        ? 'bg-brand text-white'
                                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                      {page}
                                    </button>
                                  );
                                })}
                              </div>

                              <button
                                onClick={() => setOpportunityPagination(prev => ({ ...prev, page: currentPage + 1 }))}
                                disabled={!opportunityPaginationData.hasNextPage || currentPage >= totalPages}
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}

                {/* Customers Tab Content */}
                {modalActiveTab === 'customers' && (
                  <>
                    {/* Loading State */}
                    {loadingCustomers && (
                      <div className="divide-y divide-gray-200">
                        {[...Array(5)].map((_, index) => (
                          <OpportunityRowSkeleton key={index} />
                        ))}
                      </div>
                    )}

                    {/* Error State */}
                    {customerError && !loadingCustomers && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <div className="flex items-start space-x-3">
                          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                          <div>
                            <h3 className="text-sm font-medium text-red-800">Error Loading Customers</h3>
                            <p className="text-sm text-red-700 mt-1">{customerError}</p>
                            <button
                              onClick={() => refetchCustomers()}
                              className="btn-cancel mt-4"
                            >
                              Retry
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Empty State */}
                    {!loadingCustomers && !customerError && customers.length === 0 && (
                      <div className="text-center py-12">
                        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {customerSearchTerm.trim() ? 'No customers found' : 'No Customers Available'}
                        </h3>
                        <p className="text-gray-600">
                          {customerSearchTerm.trim()
                            ? `No customers match your search "${customerSearchTerm}". Try adjusting your search terms.`
                            : 'There are no customers available for creating DOIs at this time.'}
                        </p>
                      </div>
                    )}

                    {/* Customers List */}
                    {!loadingCustomers && !customerError && customers.length > 0 && (
                      <div className="space-y-3">
                        {customers.map((customer) => {
                          const companyName = customer.companyName || 'N/A';
                          const contactPerson = customer.contactPerson || 'N/A';
                          const email = customer.email || '';
                          const phone = customer.phone || '';

                          return (
                            <div
                              key={customer._id || customer.id}
                              onClick={() => {
                                handleSelectCustomer(customer._id || customer.id);
                              }}
                              className={`
                              border border-gray-200 rounded-lg p-4 transition-all duration-200
                              bg-white hover:bg-brand/5 hover:border-brand cursor-pointer hover:shadow-md
                            `}
                            >
                              <div className="flex items-start justify-between">
                                {/* Left Section - Company & Contact Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-1">
                                      <Building className="h-5 w-5 text-brand" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h3
                                        className="text-base font-semibold text-gray-900 truncate capitalize"
                                        title={companyName}
                                      >
                                        {companyName}
                                      </h3>
                                      <div className="mt-2 flex flex-wrap items-center gap-4">
                                        <div className="flex items-center space-x-2 min-w-0">
                                          <User className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                          <span
                                            className="text-sm text-gray-700 truncate capitalize"
                                            title={contactPerson}
                                          >
                                            {contactPerson}
                                          </span>
                                        </div>
                                        {email && (
                                          <span
                                            className="text-xs text-gray-500 truncate"
                                            title={email}
                                          >
                                            {email}
                                          </span>
                                        )}
                                        {phone && (
                                          <span
                                            className="text-xs text-gray-500 truncate"
                                            title={phone}
                                          >
                                            {phone}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Right Section - Customer ID */}
                                <div className="flex items-center space-x-4 ml-4 flex-shrink-0">
                                  <div className="text-right min-w-0">
                                    <div
                                      className="text-sm font-medium text-gray-600 truncate"
                                      title={customer.customerId || 'N/A'}
                                    >
                                      {customer.customerId || 'N/A'}
                                    </div>
                                  </div>
                                  <div className="flex-shrink-0">
                                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand/10 text-brand">
                                      <CheckCircle className="h-5 w-5" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Pagination */}
                    {!loadingCustomers && !customerError && customers.length > 0 && (() => {
                      const totalItems = customerPagination.totalCustomers || customerPaginationData.total || customerPaginationData.totalCustomers || customers.length;
                      const totalPages = customerPagination.totalPages || customerPaginationData.totalPages || 1;
                      const effectiveLimit = customerPagination.limit || customerPaginationData.limit || customerItemsPerPage;
                      const shouldShowPagination = totalPages > 1;
                      const currentPage = customerPagination.page || customerPaginationData.page || 1;

                      const handlePageChange = (page) => {
                        setCustomerPagination(prev => ({ ...prev, page }));
                      };

                      if (!shouldShowPagination) return null;

                      // Generate page numbers
                      const pages = [];
                      if (totalPages <= 10) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push(1);
                        if (currentPage <= 4) {
                          for (let i = 2; i <= 5; i++) {
                            pages.push(i);
                          }
                          pages.push('ellipsis');
                          pages.push(totalPages);
                        } else if (currentPage >= totalPages - 3) {
                          pages.push('ellipsis');
                          for (let i = totalPages - 4; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push('ellipsis');
                          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                            pages.push(i);
                          }
                          pages.push('ellipsis');
                          pages.push(totalPages);
                        }
                      }

                      return (
                        <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                          <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                            {/* Results Count and Limit Selector */}
                            <div className="flex items-center space-x-4">
                              <div className="text-sm text-gray-700">
                                Showing {formatIndianNumber(((currentPage - 1) * effectiveLimit) + 1)} to {formatIndianNumber(Math.min(currentPage * effectiveLimit, totalItems))} of {formatIndianNumber(totalItems)} results
                              </div>
                              <div className="flex items-center space-x-2">
                                <label className="text-sm text-gray-700">Items per page:</label>
                                <select
                                  value={customerItemsPerPage}
                                  onChange={(e) => handleCustomerItemsPerPageChange(Number(e.target.value))}
                                  className="input-field px-2 py-1 text-sm h-8"
                                >
                                  <option value={3}>3</option>
                                  <option value={10}>10</option>
                                  <option value={25}>25</option>
                                  <option value={50}>50</option>
                                  <option value={100}>100</option>
                                </select>
                              </div>
                            </div>

                            {/* Pagination Controls */}
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={!customerPagination.hasPrevPage || currentPage <= 1}
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Previous
                              </button>

                              {/* Page Numbers */}
                              <div className="flex items-center space-x-1">
                                {pages.map((page, index) => {
                                  if (page === 'ellipsis') {
                                    return (
                                      <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                        ...
                                      </span>
                                    );
                                  }

                                  return (
                                    <button
                                      key={page}
                                      onClick={() => setCustomerPagination(prev => ({ ...prev, page }))}
                                      className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPage
                                        ? 'bg-brand text-white'
                                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                      {page}
                                    </button>
                                  );
                                })}
                              </div>

                              <button
                                onClick={() => setCustomerPagination(prev => ({ ...prev, page: currentPage + 1 }))}
                                disabled={!customerPaginationData.hasNextPage || currentPage >= totalPages}
                                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end p-6 border-t border-gray-200">
                <button
                  className="btn-cancel"
                  onClick={handleCloseOpportunityModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {doiToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Confirm Deletion</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete DOI{' '}
                <span className="font-bold">{doiToDelete.doiNumber || 'this record'}</span>? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  onClick={handleCancelDeleteQuote}
                  disabled={!!deletingDoiId}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                  onClick={confirmDeleteQuote}
                  disabled={!!deletingDoiId}
                >
                  {deletingDoiId ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  <span>{deletingDoiId ? 'Deleting...' : 'Delete'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Confirm Bulk Deletion</h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete{' '}
                <span className="font-bold">{selectedRows.size}</span> selected DOI{selectedRows.size > 1 ? 's' : ''}? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  className="btn-cancel"
                  onClick={() => setShowBulkDeleteModal(false)}
                  disabled={isBulkDeleting}
                >
                  Cancel
                </button>
                <button
                  className="btn-danger flex items-center justify-center space-x-2"
                  onClick={confirmBulkDelete}
                  disabled={isBulkDeleting}
                >
                  {isBulkDeleting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  <span>{isBulkDeleting ? 'Deleting...' : 'Delete Selected'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        onExportRange={handleExportRange}
        loading={exportLoading}
        title="Export DOIs"
        showDateRange={true}
        allowFutureDates={true}
      />
    </div >
  );
};

export default CreateQuotesList;
